/**
 * Zustand store for the globally-mounted `SyncDataDialog` (Slice 10b).
 *
 * Any surface that needs to open the dialog (Database Blueprint toolbar today,
 * command palette / context menus later) calls
 * `useSyncDataDialogStore.getState().openSyncDataDialog(folderId)`. The
 * dialog lives at the App root (see `GlobalSyncDataDialog`) so callers
 * don't need to host the dialog themselves and we can re-open it from
 * anywhere.
 *
 * Blueprints the lighter shape used by other in-flight Database Blueprint dialogs:
 * the store only carries the folder identity. The host resolves the full
 * mounted-folder record from the script-browser store at render time so we
 * always see the freshest metadata (linkedDbs, activeLinkLabel, …).
 */
import { create } from 'zustand';

export type SyncDataDialogState =
  | { open: false }
  | {
      open: true;
      folderId: string;
      /** Workspace tab ID — used with `activeLinkConnectionId` to queue a
       *  pending retry in `pendingRetryStore` when `listBlueprintDataTables`
       *  fails with a connection-lost error. Optional: callers that cannot
       *  resolve the tab ID omit it; the error falls through to inline display. */
      tabId?: string;
      /** ConnectionId of the active link — required alongside `tabId` to
       *  populate the `requires` array of the pending retry. */
      activeLinkConnectionId?: string;
    };

export type SyncDataDialogOpenOpts = {
  tabId?: string;
  activeLinkConnectionId?: string;
};

export interface SyncDataDialogStore {
  state: SyncDataDialogState;
  openSyncDataDialog: (folderId: string, opts?: SyncDataDialogOpenOpts) => void;
  closeSyncDataDialog: () => void;
}

export const useSyncDataDialogStore = create<SyncDataDialogStore>((set) => ({
  state: { open: false },
  openSyncDataDialog: (folderId: string, opts?: SyncDataDialogOpenOpts) =>
    set({
      state: {
        open: true,
        folderId,
        tabId: opts?.tabId,
        activeLinkConnectionId: opts?.activeLinkConnectionId,
      },
    }),
  closeSyncDataDialog: () => set({ state: { open: false } }),
}));
